import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { StatutStockAppareil, TypeMessageMontant } from '@genviz/shared/enums';
import type {
  CommandeDiagnostic,
  DesassociationForceeReponse,
  ResumeAlerte,
  TrameBrute,
} from '@genviz/shared/dto';

import { useDesassocier, useDiagnostic, useMesures } from '../api/queries';
import { Capacite } from '../auth/permissions';
import { usePeut } from '../auth/useSession';
import {
  Badge,
  BadgeAlimentation,
  BadgeConnectivite,
  BadgeCriticite,
  BadgeStatutCommande,
  BadgeStatutStock,
  BadgeSynchronisation,
} from '../components/Badges';
import { Bouton, BoutonLien } from '../components/Boutons';
import { Carte, Definitions } from '../components/Carte';
import { Interrupteur, Saisie, Segmente, Selecteur, ZoneTexte } from '../components/Champs';
import { Chargement } from '../components/Chargement';
import { CourbeTension } from '../components/Graphes';
import { JsonBrut } from '../components/JsonBrut';
import { MessageErreur, texteErreur } from '../components/MessageErreur';
import { Modale } from '../components/Modale';
import { useNotifications } from '../components/Notifications';
import { EtalonnageTension } from '../components/EtalonnageTension';
import { Etiquette } from '../components/QrCode';
import { Tableau, type Colonne } from '../components/Tableau';
import {
  LIBELLE_CAUSE_ECHEC,
  LIBELLE_MODELE,
  LIBELLE_TYPE_ALERTE,
  LIBELLE_TYPE_COMMANDE,
  LIBELLE_TYPE_MESSAGE,
  VALEUR_ABSENTE,
} from '../i18n/fr';
import {
  formaterDate,
  formaterDateHeure,
  formaterDateHeureEtRelatif,
  formaterDateHeureSecondes,
  formaterDbm,
  formaterDecimal,
  formaterRelatif,
  ouAbsent,
  versDate,
} from '../lib/format';
import type { CauseEchecCommande } from '@genviz/shared/enums';

type Fenetre = '1h' | '24h' | '7j';

const FENETRES: ReadonlyArray<{ valeur: Fenetre; libelle: string; minutes: number }> = [
  { valeur: '1h', libelle: '1 h', minutes: 60 },
  { valeur: '24h', libelle: '24 h', minutes: 1440 },
  { valeur: '7j', libelle: '7 j', minutes: 10_080 },
];

/**
 * Fiche appareil — l'écran de diagnostic du support.
 *
 * Il est organisé dans l'ordre des questions posées par un ticket : « l'appareil parle-t-il ? »
 * (heartbeat, RSSI, trames), « que mesure-t-il ? » (courbe de tension et seuils), « a-t-il reçu
 * ce qu'on lui a envoyé ? » (paramétrage souhaité contre appliqué, commandes et acquittements),
 * « qu'est-ce qui a été signalé ? » (alertes actives).
 *
 * La divergence de configuration est mise en évidence : c'est la panne la plus fréquente et la
 * moins visible — un objet qui répond mais applique un ancien seuil de gardiennage produit des
 * alertes incohérentes avec ce que l'exploitant lit dans l'application.
 */
export function AppareilPage(): JSX.Element {
  const { numeroSerie = '' } = useParams();
  const sn = decodeURIComponent(numeroSerie);
  const peutDesassocier = usePeut(Capacite.DESASSOCIER);

  const [fenetre, setFenetre] = useState<Fenetre>('24h');
  const [limiteTrames, setLimiteTrames] = useState(50);
  const [typeTrame, setTypeTrame] = useState('');
  const [trameOuverte, setTrameOuverte] = useState<TrameBrute | null>(null);
  const [desassociationOuverte, setDesassociationOuverte] = useState(false);

  const minutes = FENETRES.find((f) => f.valeur === fenetre)?.minutes ?? 1440;
  const depuis = useMemo(
    () => new Date(Date.now() - minutes * 60_000).toISOString(),
    [minutes],
  );

  const diagnostic = useDiagnostic(sn, { depuis, limiteTrames });
  const mesures = useMesures(sn, { depuis, max_points: 200 });

  if (diagnostic.isPending) return <Chargement libelle={`Diagnostic de ${sn}…`} />;
  if (diagnostic.error) return <MessageErreur erreur={diagnostic.error} />;
  if (!diagnostic.data) return <Chargement libelle={`Diagnostic de ${sn}…`} />;

  const d = diagnostic.data;
  const horloge = versDate(d.genere_at) ?? new Date();
  const appaire = d.appareil.statut === StatutStockAppareil.ENREGISTRE;

  const tramesFiltrees = typeTrame
    ? d.dernieres_trames.filter((t) => t.type === typeTrame)
    : d.dernieres_trames;

  const parametrage = d.parametrage;
  const divergences: Array<{ champ: string; souhaite: string; applique: string }> = parametrage
    ? [
        {
          champ: 'Version de configuration',
          souhaite: String(parametrage.souhaite.version),
          applique: parametrage.applique.version === null ? VALEUR_ABSENTE : String(parametrage.applique.version),
        },
        {
          champ: 'Tension de gardiennage',
          souhaite: `${formaterDecimal(parametrage.souhaite.tension_gardiennage_kv)} kV`,
          applique:
            parametrage.applique.tension_gardiennage_kv === null
              ? VALEUR_ABSENTE
              : `${formaterDecimal(parametrage.applique.tension_gardiennage_kv)} kV`,
        },
        {
          champ: 'Fréquence de géolocalisation',
          souhaite: `${parametrage.souhaite.frequence_geoloc_min} min`,
          applique:
            parametrage.applique.frequence_geoloc_min === null
              ? VALEUR_ABSENTE
              : `${parametrage.applique.frequence_geoloc_min} min`,
        },
        {
          champ: 'Surveillance de mouvement',
          souhaite: parametrage.souhaite.surveillance_mouvement ? 'Activée' : 'Désactivée',
          applique:
            parametrage.applique.surveillance_mouvement === null
              ? VALEUR_ABSENTE
              : parametrage.applique.surveillance_mouvement
                ? 'Activée'
                : 'Désactivée',
        },
      ]
    : [];

  const colonnesCommandes: Array<Colonne<CommandeDiagnostic>> = [
    { cle: 'type', entete: 'Commande', rendu: (c) => LIBELLE_TYPE_COMMANDE[c.type] },
    { cle: 'statut', entete: 'Statut', rendu: (c) => <BadgeStatutCommande statut={c.statut} /> },
    { cle: 'creee', entete: 'Créée', rendu: (c) => formaterDateHeure(c.created_at) },
    { cle: 'envoyee', entete: 'Envoyée', rendu: (c) => formaterDateHeure(c.envoyee_at) },
    { cle: 'acquittee', entete: 'Acquittée', rendu: (c) => formaterDateHeure(c.acquittee_at) },
    {
      cle: 'appliquee',
      entete: 'Appliquée',
      rendu: (c) => formaterDateHeure(c.appliquee_at),
    },
    { cle: 'tentatives', entete: 'Tent.', rendu: (c) => c.tentatives, numerique: true },
    {
      cle: 'erreur',
      entete: 'Cause d’échec',
      rendu: (c) =>
        c.code_erreur
          ? (LIBELLE_CAUSE_ECHEC[c.code_erreur as CauseEchecCommande] ?? c.code_erreur)
          : VALEUR_ABSENTE,
    },
    {
      cle: 'parametres',
      entete: 'Paramètres',
      rendu: (c) =>
        Object.keys(c.parametres).length === 0 ? (
          VALEUR_ABSENTE
        ) : (
          <span className="gv-mono gv-corps-s">{JSON.stringify(c.parametres)}</span>
        ),
    },
  ];

  const colonnesTrames: Array<Colonne<TrameBrute>> = [
    { cle: 'type', entete: 'Type', rendu: (t) => LIBELLE_TYPE_MESSAGE[t.type] },
    {
      cle: 'emis',
      entete: 'Émise (objet)',
      rendu: (t) => formaterDateHeureSecondes(t.emis_at),
    },
    { cle: 'recu', entete: 'Reçue (serveur)', rendu: (t) => formaterDateHeureSecondes(t.recu_at) },
    { cle: 'boot', entete: 'Boot', rendu: (t) => t.boot, numerique: true },
    { cle: 'seq', entete: 'Seq', rendu: (t) => t.seq, numerique: true },
    {
      cle: 'signature',
      entete: 'Signature',
      rendu: (t) =>
        t.signature_valide ? (
          <Badge ton="vert" libelle="Valide" />
        ) : (
          <Badge ton="rouge" libelle="Invalide" />
        ),
    },
    {
      cle: 'traitement',
      entete: 'Traitement',
      rendu: (t) =>
        t.erreur_traitement ? (
          <span className="gv-mono" style={{ color: 'var(--gv-rouge)' }}>
            {t.erreur_traitement}
          </span>
        ) : (
          formaterDateHeureSecondes(t.traite_at)
        ),
    },
    {
      cle: 'charge',
      entete: '',
      rendu: (t) => (
        <Bouton
          petit
          variante="discret"
          onClick={(e) => {
            e.stopPropagation();
            setTrameOuverte(t);
          }}
        >
          Charge utile
        </Bouton>
      ),
    },
  ];

  const colonnesAlertes: Array<Colonne<ResumeAlerte>> = [
    { cle: 'numero', entete: 'N°', rendu: (a) => a.numero, numerique: true },
    { cle: 'type', entete: 'Type', rendu: (a) => LIBELLE_TYPE_ALERTE[a.type] },
    { cle: 'criticite', entete: 'Criticité', rendu: (a) => <BadgeCriticite criticite={a.criticite} /> },
    {
      cle: 'declenchee',
      entete: 'Déclenchée',
      rendu: (a) => (
        <span title={formaterDateHeure(a.declenchee_at)}>
          {formaterRelatif(a.declenchee_at, horloge)}
        </span>
      ),
    },
  ];

  return (
    <>
      <header className="gv-entete">
        <div className="gv-pile gv-pile--serree">
          <h1 className="gv-h1 gv-mono">{d.appareil.numero_serie_electrificateur}</h1>
          <div className="gv-ligne">
            <BadgeStatutStock statut={d.appareil.statut} />
            <BadgeConnectivite etat={d.connectivite} />
            <BadgeAlimentation etat={d.etat_alimentation} />
            <span className="gv-corps-s gv-secondaire">
              {LIBELLE_MODELE[d.appareil.modele]}
              {d.nom_electrificateur ? ` · « ${d.nom_electrificateur} »` : ''}
            </span>
          </div>
        </div>
        <div className="gv-entete__actions">
          <Segmente
            libelle="Fenêtre d’analyse"
            valeur={fenetre}
            options={FENETRES.map((f) => ({ valeur: f.valeur, libelle: f.libelle }))}
            onChange={setFenetre}
          />
          <BoutonLien vers={`/appareils/${encodeURIComponent(sn)}/donnees`} variante="primaire">
            Toutes les données
          </BoutonLien>
          <Bouton
            occupe={diagnostic.isFetching}
            onClick={() => {
              void diagnostic.refetch();
              void mesures.refetch();
            }}
          >
            Actualiser
          </Bouton>
          {peutDesassocier ? (
            <Bouton
              variante="danger"
              disabled={!appaire}
              title={appaire ? undefined : 'L’appareil n’est associé à aucune exploitation.'}
              onClick={() => {
                setDesassociationOuverte(true);
              }}
            >
              Désassocier / SAV
            </Bouton>
          ) : null}
        </div>
      </header>

      <div className="gv-grille gv-grille--2">
        <Carte titre="Identité et rattachement">
          <Definitions
            entrees={[
              {
                cle: 'Exploitation',
                valeur: d.exploitation ? (
                  <Link to={`/exploitations/${d.exploitation.id}`}>{d.exploitation.nom}</Link>
                ) : (
                  VALEUR_ABSENTE
                ),
              },
              { cle: 'Nom donné par l’exploitant', valeur: ouAbsent(d.nom_electrificateur) },
              {
                cle: 'Module de connectivité',
                valeur: <span className="gv-mono">{d.appareil.numero_serie_module}</span>,
              },
              { cle: 'IMEI', valeur: <span className="gv-mono">{ouAbsent(d.appareil.imei)}</span> },
              { cle: 'ICCID', valeur: <span className="gv-mono">{ouAbsent(d.appareil.iccid_masque)}</span> },
              { cle: 'Lot', valeur: ouAbsent(d.appareil.lot) },
              { cle: 'Fabrication', valeur: formaterDate(d.appareil.date_fabrication) },
              { cle: 'Appairé le', valeur: formaterDateHeure(d.appareil.associe_at) },
            ]}
          />
        </Carte>

        <Carte titre="Liaison radio et firmware">
          <Definitions
            entrees={[
              {
                cle: 'Dernier heartbeat',
                valeur: formaterDateHeureEtRelatif(d.dernier_heartbeat_at, horloge),
              },
              {
                cle: 'Dernière mesure',
                valeur: formaterDateHeureEtRelatif(d.derniere_mesure_at, horloge),
              },
              { cle: 'RSSI', valeur: formaterDbm(d.rssi_dbm) },
              { cle: 'Firmware embarqué', valeur: ouAbsent(d.firmware_version) },
              { cle: 'Firmware usine', valeur: ouAbsent(d.appareil.firmware_usine) },
              {
                cle: 'Synchronisation',
                valeur: parametrage ? (
                  <BadgeSynchronisation statut={parametrage.statut_sync} />
                ) : (
                  VALEUR_ABSENTE
                ),
              },
              {
                cle: 'Dernière synchro',
                valeur: parametrage ? formaterDateHeure(parametrage.derniere_sync_at) : VALEUR_ABSENTE,
              },
            ]}
          />
        </Carte>
      </div>

      <Carte
        titre="Tension d’impulsion"
        sousTitre={`Fenêtre ${FENETRES.find((f) => f.valeur === fenetre)?.libelle ?? ''} · seuils du cadran de l’exploitant`}
      >
        {mesures.error ? (
          <MessageErreur erreur={mesures.error} />
        ) : mesures.data ? (
          <CourbeTension points={mesures.data.points} reperes={mesures.data.reperes} />
        ) : (
          <Chargement libelle="Chargement des mesures…" />
        )}
      </Carte>

      {parametrage ? (
        <Carte
          titre="Paramétrage"
          sousTitre="Souhaité par le backend contre appliqué par l’objet"
          actions={<BadgeSynchronisation statut={parametrage.statut_sync} />}
          sansPadding
        >
          <div className="gv-table-cadre">
            <table className="gv-table">
              <thead>
                <tr>
                  <th>Paramètre</th>
                  <th>Souhaité</th>
                  <th>Appliqué</th>
                </tr>
              </thead>
              <tbody>
                {divergences.map((ligne) => {
                  const diverge = ligne.souhaite !== ligne.applique;
                  return (
                    <tr key={ligne.champ}>
                      <td>{ligne.champ}</td>
                      <td className="gv-mono">{ligne.souhaite}</td>
                      <td
                        className="gv-mono"
                        style={diverge ? { color: 'var(--gv-rouge)', fontWeight: 700 } : undefined}
                      >
                        {ligne.applique}
                        {diverge ? ' ⚠' : ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Carte>
      ) : null}

      {appaire ? <EtalonnageTension numeroSerie={sn} /> : null}

      <Carte
        titre="Alertes actives"
        sousTitre="Telles que l’exploitant les voit dans l’application"
        sansPadding
      >
        <Tableau
          colonnes={colonnesAlertes}
          lignes={d.alertes_actives}
          cleDe={(a) => a.id}
          titreVide="Aucune alerte active"
          descriptionVide="L’appareil est nominal du point de vue du moteur d’alertes."
        />
      </Carte>

      <Carte
        titre="Commandes et acquittements"
        sousTitre="ACK = reçue par l’objet · Appliquée = état réel confirmé par une remontée"
        sansPadding
      >
        <Tableau
          colonnes={colonnesCommandes}
          lignes={d.commandes}
          cleDe={(c) => c.id}
          titreVide="Aucune commande"
          descriptionVide="Aucune commande descendante n’a été émise vers cet appareil."
        />
      </Carte>

      <Carte
        titre="Trames brutes"
        sousTitre={
          <>
            Journal d’ingestion, conservé 30 jours ·{' '}
            <Link to={`/appareils/${encodeURIComponent(sn)}/donnees`}>tout l’historique et l’export</Link>
          </>
        }
        actions={
          <>
            <Selecteur
              label="Type"
              optionVide="Tous les types"
              options={Object.values(TypeMessageMontant).map((t) => ({
                valeur: t,
                libelle: LIBELLE_TYPE_MESSAGE[t],
              }))}
              value={typeTrame}
              onChange={(e) => {
                setTypeTrame(e.currentTarget.value);
              }}
            />
            <Selecteur
              label="Profondeur"
              options={[50, 100, 250, 500].map((n) => ({ valeur: String(n), libelle: `${n} trames` }))}
              value={String(limiteTrames)}
              onChange={(e) => {
                setLimiteTrames(Number(e.currentTarget.value));
              }}
            />
          </>
        }
        sansPadding
      >
        <Tableau
          colonnes={colonnesTrames}
          lignes={tramesFiltrees}
          cleDe={(t) => String(t.id)}
          chargement={diagnostic.isFetching}
          titreVide="Aucune trame"
          descriptionVide="Aucune trame reçue sur la fenêtre demandée. Élargissez la fenêtre ou vérifiez la connectivité."
        />
      </Carte>

      <Modale
        titre={trameOuverte ? `Charge utile — ${LIBELLE_TYPE_MESSAGE[trameOuverte.type]}` : ''}
        ouverte={trameOuverte !== null}
        surFermeture={() => {
          setTrameOuverte(null);
        }}
        large
      >
        {trameOuverte ? (
          <>
            <Definitions
              entrees={[
                { cle: 'Identifiant de message', valeur: <span className="gv-mono">{trameOuverte.message_id}</span> },
                { cle: 'Boot / séquence', valeur: `${trameOuverte.boot} / ${trameOuverte.seq}` },
                { cle: 'Émise', valeur: formaterDateHeureSecondes(trameOuverte.emis_at) },
                { cle: 'Reçue', valeur: formaterDateHeureSecondes(trameOuverte.recu_at) },
              ]}
            />
            <JsonBrut valeur={trameOuverte.payload} libelleCopie="Copier la charge utile" />
          </>
        ) : null}
      </Modale>

      {desassociationOuverte ? (
        <ModaleDesassociation
          numeroSerie={d.appareil.numero_serie_electrificateur}
          moduleActuel={d.appareil.numero_serie_module}
          surFermeture={() => {
            setDesassociationOuverte(false);
          }}
        />
      ) : null}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Désassociation forcée                                                       */
/* -------------------------------------------------------------------------- */

/**
 * Désassociation forcée : litige, retour SAV, remplacement du module.
 *
 * Opération destructive et visible côté client — l'appareil disparaît de son application. La
 * boîte exige donc un motif (journal d'audit) et affiche le nouveau secret d'appairage UNE
 * SEULE FOIS, avec son QR : c'est la seule occasion de le capturer.
 */
function ModaleDesassociation({
  numeroSerie,
  moduleActuel,
  surFermeture,
}: {
  numeroSerie: string;
  moduleActuel: string;
  surFermeture: () => void;
}): JSX.Element {
  const notifications = useNotifications();
  const mutation = useDesassocier(numeroSerie);

  const [motif, setMotif] = useState('');
  const [remplacerModule, setRemplacerModule] = useState(false);
  const [nouveauModule, setNouveauModule] = useState('');
  const [regenerer, setRegenerer] = useState(true);
  const [resultat, setResultat] = useState<DesassociationForceeReponse | null>(null);

  const valide =
    motif.trim().length >= 3 && (!remplacerModule || nouveauModule.trim().length >= 3);

  if (resultat) {
    return (
      <Modale
        titre="Appareil désassocié"
        ouverte
        surFermeture={surFermeture}
        pied={
          <Bouton variante="primaire" onClick={surFermeture}>
            Terminer
          </Bouton>
        }
      >
        <div className="gv-bandeau gv-bandeau--succes">
          {numeroSerie} est repassé en stock. {resultat.commandes_annulees} commande(s) en vol
          annulée(s).
        </div>

        {resultat.nouveau_secret_appairage ? (
          <>
            <p className="gv-corps-s">
              Nouveau secret d’appairage. Il ne sera plus jamais affiché : imprimez l’étiquette
              maintenant, l’ancien QR code est caduc.
            </p>
            <div className="gv-planche">
              <Etiquette
                etiquette={{
                  numeroSerie,
                  secret: resultat.nouveau_secret_appairage,
                }}
              />
            </div>
            <Bouton
              variante="discret"
              onClick={() => {
                window.print();
              }}
            >
              Imprimer l’étiquette
            </Bouton>
          </>
        ) : (
          <p className="gv-corps-s gv-secondaire">
            Le secret d’appairage n’a pas été régénéré : l’étiquette existante reste valable.
          </p>
        )}
      </Modale>
    );
  }

  return (
    <Modale
      titre={`Désassocier ${numeroSerie}`}
      ouverte
      surFermeture={surFermeture}
      pied={
        <>
          <Bouton variante="discret" onClick={surFermeture}>
            Annuler
          </Bouton>
          <Bouton
            variante="danger"
            disabled={!valide}
            occupe={mutation.isPending}
            onClick={() => {
              mutation.mutate(
                {
                  motif: motif.trim(),
                  ...(remplacerModule ? { remplacer_module_par: nouveauModule.trim() } : {}),
                  regenerer_secret_appairage: regenerer,
                },
                {
                  onSuccess: (reponse) => {
                    setResultat(reponse);
                    notifications.succes(`${numeroSerie} désassocié et remis en stock.`);
                  },
                  onError: (erreur) => {
                    notifications.erreur(texteErreur(erreur));
                  },
                },
              );
            }}
          >
            Désassocier
          </Bouton>
        </>
      }
    >
      <div className="gv-bandeau gv-bandeau--attention">
        L’appareil sera retiré de l’exploitation et disparaîtra de l’application de l’exploitant.
        Ses commandes en vol seront annulées. L’historique de mesures et d’alertes est conservé.
      </div>

      <ZoneTexte
        label="Motif"
        aide="Obligatoire, consigné au journal d’audit. 200 caractères maximum."
        maxLength={200}
        value={motif}
        onChange={(e) => {
          setMotif(e.currentTarget.value);
        }}
      />

      <Interrupteur
        libelle="Remplacer le module de connectivité (échange SAV)"
        actif={remplacerModule}
        onChange={setRemplacerModule}
      />

      {remplacerModule ? (
        <Saisie
          label="Numéro de série du module de remplacement"
          aide={`Module actuel : ${moduleActuel}`}
          value={nouveauModule}
          onChange={(e) => {
            setNouveauModule(e.currentTarget.value);
          }}
        />
      ) : null}

      <Interrupteur
        libelle="Régénérer le secret d’appairage (invalide le QR code imprimé)"
        actif={regenerer}
        onChange={setRegenerer}
      />

      {mutation.error ? <MessageErreur erreur={mutation.error} /> : null}
    </Modale>
  );
}
